
import { Component, Prop, Vue } from 'vue-property-decorator';
import { ACTIVE_STATUSES, STATUS_COLORS, bulkEditActionTitle, computePercent } from '@/utils/bulkJob';
import { decodeString, decodeTag } from '@/utils/decoding';
import TextButton from '@/components/shared/suites/TextButton.vue';
import IconButton from '@/components/shared/suites/IconButton.vue';
import TagPreview from '@/components/shared/TagPreview.vue';
import WarningModal from '@/components/shared/suites/WarningModal.vue';
import ItemFailuresModal from '@/components/multi-location/ItemFailuresModal.vue';

@Component<BulkEditProgress>({
	components: {
		TextButton,
		IconButton,
		TagPreview,
		WarningModal,
		ItemFailuresModal
	}
})
export default class BulkEditProgress extends Vue {
	@Prop({ type: Object, required: true }) private job!: BulkJob;
	@Prop({ type: Array, required: false, default: () => [] }) private locationTags!: MenuItemTag[];

	private showCancelConfirm: boolean = false;
	private showFailures: boolean = false;

	private get itemEdit(): BulkJobItemEdit | undefined {
		return this.job.itemEdit;
	}

	private get isActive(): boolean {
		return ACTIVE_STATUSES.includes(this.job.status);
	}

	private get statusColor(): string {
		return STATUS_COLORS[this.job.status] ?? 'grey';
	}

	private get percent(): number {
		return computePercent(this.job);
	}

	// The tag a create-tag job is creating. Null for item-edit jobs.
	private get createdTag(): MenuItemTag | null {
		return this.job.tagCreate ? decodeTag(this.job.tagCreate as MenuItemTag) : null;
	}

	private get title(): string {
		if (this.createdTag) {
			return this.$t('shared.bulk_job.titles.create_tag');
		}
		return bulkEditActionTitle(this.itemEdit);
	}

	/**
	 * "6 items • 3 locations" for item edits. A create-tag job has no items, so it reads
	 * "3 locations" on its own.
	 *
	 * @return {string}
	 */
	private get scopeText(): string {
		const locations: string = this.$tc('multi_location_settings.job.locations_count', this.job.totalLocations, { count: this.job.totalLocations });
		if (!this.itemEdit) {
			return locations;
		}
		const itemCount: number = this.itemEdit.itemCount ?? this.itemEdit.items?.length ?? 0;
		const items: string = this.$tc('multi_location_settings.job.items_count', itemCount, { count: itemCount });
		return `${items} • ${locations}`;
	}

	/**
	 * Locations that already held a tag with this name, so the tag wasn't created there. The job marks
	 * them `skipped`; the rest of the locations still got the tag.
	 *
	 * @return {string}
	 */
	private get skippedLocationNames(): string {
		if (!this.createdTag || !this.job.locations?.length) {
			return '';
		}
		return this.job.locations
			.filter(location => location.status === 'skipped')
			.map(location => decodeString(location.name))
			.join(', ');
	}

	/**
	 * Item-level progress. An operator selects items, not locations, so an item edit reports items;
	 * a create-tag job (one unit of work per location) keeps counting locations. Falls back to
	 * locations when the backend predates the item counters.
	 *
	 * @return {string}
	 */
	private get progressText(): string {
		if (this.itemEdit && this.job.itemCounts && this.job.totalItems) {
			return this.$tc('shared.bulk_job.items_succeeded', this.job.itemCounts.completed, {
				completed: this.job.itemCounts.completed,
				total: this.job.totalItems
			});
		}
		return this.$t('multi_location_settings.job.succeeded', {
			completed: this.job.counts.completed,
			total: this.job.totalLocations
		});
	}

	/**
	 * Locations with failures, with the failed items named. Best-effort jobs still mark a location
	 * `completed` — the edits that went through are kept — so without this the operator would only
	 * see "X of Y updated" and never learn which items were missed. `hidden` covers failures the
	 * backend counted but capped out of the detail list. Item-edit jobs only; the create-tag skip
	 * case is handled by `skippedLocationNames` above.
	 *
	 * @return {FailedLocationRow[]}
	 */
	private get failedLocations(): FailedLocationRow[] {
		if (!this.itemEdit || !this.job.locations?.length) {
			return [];
		}
		return this.job.locations
			.filter(location => (location.itemCounts?.failed ?? 0) > 0 || !!location.errorMessage)
			.map(location => {
				const failed: number = location.itemCounts?.failed ?? 0;
				const errors: BulkJobItemError[] = (location.itemErrors ?? [])
					.map(error => ({ ...error, name: error.name ? decodeString(error.name) : null }));
				return {
					restaurantId: location.restaurantId,
					name: decodeString(location.name),
					failed,
					total: location.totalItems ?? 0,
					errors,
					hidden: Math.max(0, failed - errors.length),
					fallbackMessage: errors.length === 0 ? location.errorMessage : null
				};
			});
	}

	/**
	 * Exact number of failed items across the job. Comes from the counters, never from the length of
	 * the error list — the backend caps detail at 50 per location and 200 per response, so the list
	 * can be far shorter than the truth.
	 *
	 * @return {number}
	 */
	private get failedItemCount(): number {
		if (this.job.itemCounts) {
			return this.job.itemCounts.failed;
		}
		return this.failedLocations.reduce((sum: number, location: FailedLocationRow) => sum + location.failed, 0);
	}

	/**
	 * The card's one-line failure banner. Deliberately does not enumerate anything: at 350 locations
	 * even the per-location headings would run off the page, and this card re-renders on every 10s poll.
	 *
	 * @return {string}
	 */
	private get failureSummary(): string {
		// A location that failed as a whole (e.g. its message never reached the queue) applied nothing
		// and has no per-item counts, so an item-shaped summary would read "0 of 971". Count the
		// locations instead.
		if (!this.failedItemCount) {
			return this.$tc('multi_location_settings.job.location_failures_summary', this.failedLocations.length, {
				count: this.failedLocations.length
			});
		}
		const total: number = this.job.totalItems ?? 0;
		return this.$tc('multi_location_settings.job.item_failures_summary', this.failedItemCount, {
			failed: this.failedItemCount,
			total
		});
	}

	private get selectedValuesLabel(): string {
		if (!this.itemEdit?.values?.length) {
			return '';
		}
		return this.$t(`multi_location_settings.job.selected_${this.itemEdit.action}`);
	}

	/**
	 * Tag chips for a tags job. Colour/icon come from the location-tags list; a tag whose name is no
	 * longer in that list (or which the list hasn't loaded yet) falls back to a neutral chip via
	 * `selectedPlainValues`, so the card never renders blank.
	 *
	 * @return {MenuItemTag[]}
	 */
	private get selectedTags(): MenuItemTag[] {
		if (this.itemEdit?.action !== 'tags') {
			return [];
		}
		const names: string[] = this.itemEdit.values ?? [];
		return this.locationTags.filter(tag => names.includes(tag.name)).map(tag => decodeTag(tag));
	}

	private get selectedPlainValues(): string[] {
		const values: string[] = this.itemEdit?.values ?? [];
		if (this.itemEdit?.action !== 'tags') {
			return values;
		}
		const resolved: Set<string> = new Set(this.locationTags.map(tag => tag.name));
		return values.filter(value => !resolved.has(value)).map(value => decodeString(value));
	}

	private confirmCancel(): void {
		this.showCancelConfirm = false;
		this.$emit('cancel', this.job.jobId);
	}
}
